/**
 * Route guards (00-contracts §14.2).
 *
 * The chain is `requireSession → loadMe → requireConsent → requireMembership`, declared once per
 * layout rather than repeated per page: a page that forgets one of them is a security hole, and
 * the only reliable way to not forget is to have nowhere to forget it.
 *
 * These mirror the server, they do not replace it (principle 7): the API enforces the same rules
 * and answers 401/403/409 regardless of what the browser believes.
 */
import {
  guestScopeFromResponse,
  type Action,
  type MeResponse,
  type OrganizationModule,
} from '@tvitvi/shared';
import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';

import { ApiError } from '@/api/errors';
import { loadShellBootstrap } from '@/api/queries/bootstrap';
import { meQueryOptions } from '@/api/queries/me';
import { qk } from '@/api/query-keys';
import { guestScopeQueryOptions } from '@/features/guest/guest-queries';

import { actorFromMe, allows, reachesIn } from './actor';
import { setActiveOrganizationId } from './active-org';
import { getGuestOrganizationId, leaveGuestOrganization, setGuestScope } from './guest-org';
import { toRedirectParam } from './redirect';
import { hasSession } from './supabase';

export const CONSENT_ROUTE = '/onboarding/consent';
/** 284.5 (1151): siden med «Angre» for en konto som venter på slettingen. Lenken i e-posten går hit. */
export const ACCOUNT_CLOSING_ROUTE = '/konto/angre';
/** Ikke «lag et hus» lenger, men «du er ikke med i noe hus» — se `requireMembership` og sak B3. */
export const NO_ORGANIZATION_ROUTE = '/onboarding/organization';

/** Anonymous-only pages. Someone already signed in has no business on the sign-in form. */
export async function requireAnonymous(): Promise<void> {
  if (await hasSession()) {
    throw redirect({ to: '/' });
  }
}

/** Everything behind the wall. Remembers where the user was going. */
export async function requireSession(location: { href: string }): Promise<void> {
  if (!(await hasSession())) {
    throw redirect({ to: '/sign-in', search: { redirect: toRedirectParam(location.href) } });
  }
}

/**
 * One `/v1/me` for the whole authenticated subtree; a cached `me` costs nothing.
 *
 * GJESTEMODUS (sak 232, T5). Står fanen som gjest, må `me` være hentet MED gjesteorganisasjonen i
 * headeren og si `guest` for den — ellers er gjestemodus foreldet (delingen er avsluttet, eller en
 * `me` fra før fanen gikk i gjestemodus ligger i cachen). Da spørres én gang til; sier serveren
 * fortsatt ikke gjest, går fanen ut av gjestemodus og tilbake til personens egne organisasjoner.
 * Delingene (`guestScope`) hentes før noen rute spør `can()`, så knappene «kan endre» gir står der
 * fra første tegning.
 */
export async function loadMe(queryClient: QueryClient): Promise<MeResponse> {
  const guest = getGuestOrganizationId();
  if (guest === null) {
    /*
     * FØRSTE LAST: skallets seks GET-er i ett kall (`loadShellBootstrap`), som også legger `me` i
     * cachen. Feiler det — hva som helst, også et API som ennå ikke har ruten — går vi den gamle
     * veien under, og skallet henter delene sine selv som før. Gjestemodus går alltid den gamle
     * veien: klokka står i et annet hus enn headeren, og `me` må hentes på nytt uansett.
     */
    if (queryClient.getQueryData(qk.me()) === undefined) {
      try {
        await loadShellBootstrap(queryClient);
      } catch {
        // Bevisst stille: reserveveien under er den samme som før kallet fantes.
      }
    }
    return settleActiveOrganization(
      queryClient,
      await queryClient.ensureQueryData(meQueryOptions()),
    );
  }

  let me = await queryClient.ensureQueryData(meQueryOptions());

  const isGuestHere = (candidate: MeResponse): boolean =>
    candidate.activeOrgRole === 'guest' && candidate.activeOrganizationId === guest;
  if (!isGuestHere(me)) me = await queryClient.fetchQuery({ ...meQueryOptions(), staleTime: 0 });
  if (!isGuestHere(me)) {
    leaveGuestOrganization();
    return settleActiveOrganization(
      queryClient,
      await queryClient.fetchQuery({ ...meQueryOptions(), staleTime: 0 }),
    );
  }

  const scope = await queryClient.ensureQueryData(guestScopeQueryOptions(guest));
  setGuestScope(guest, guestScopeFromResponse(scope));
  return me;
}

function isMembershipOf(me: MeResponse, organizationId: string | null): boolean {
  return (
    organizationId !== null &&
    me.memberships.some((membership) => membership.organizationId === organizationId)
  );
}

/**
 * DEN LAGREDE ORGANISASJONEN KAN VÆRE ET HUS MAN NETTOPP HAR FORLATT (QA6 K2, funn 9).
 *
 * `/v1/me` er en `optional`-rute: navngir headeren et hus personen ikke lenger er medlem av
 * (deaktivert, fjernet), svarer den 200 med `activeOrganizationId: null`, ingen rolle og ingen
 * celler — ikke 403. `requireMembership` valgte da riktig hus og skrev det til localStorage, men
 * skallet tegnet den `me`-en som var hentet med det GAMLE huset i headeren: «— Ingen
 * organisasjoner», bare «Oversikt» i menyen og en tom forside, til noe annet tilfeldigvis hentet
 * `me` på nytt. Det samme skjer med to hus og ingen gyldig «sist aktiv»: serveren gjetter ikke.
 *
 * Så rettes hintet FØR skallet ser `me`, og `me` hentes én gang til med riktig hus i headeren.
 * Ikke mens et samtykke står ute: da svarer serveren uten aktør for alle hus, og det er riktig.
 */
async function settleActiveOrganization(
  queryClient: QueryClient,
  me: MeResponse,
): Promise<MeResponse> {
  const first = me.memberships.at(0);
  if (first === undefined || me.pendingConsents.length > 0) return me;
  if (isMembershipOf(me, me.activeOrganizationId)) return me;
  setActiveOrganizationId(first.organizationId);
  return queryClient.fetchQuery({ ...meQueryOptions(), staleTime: 0 });
}

/**
 * 284.5 (1151): en konto som venter på slettingen, har bare én side. Serveren sperrer alt annet
 * (`ACCOUNT_CLOSING`); dette sparer personen for å gå inn i den veggen.
 */
export function requireAccountOpen(me: MeResponse, pathname: string): void {
  const closing = me.accountDeletion !== undefined && me.accountDeletion !== null;
  if (closing && pathname !== ACCOUNT_CLOSING_ROUTE) {
    throw redirect({ to: ACCOUNT_CLOSING_ROUTE });
  }
}

/**
 * Consent is enforced server-side — every org-scoped endpoint answers 403 CONSENT_REQUIRED while
 * something is outstanding (§13.1). This only saves the user from walking into that wall.
 */
export function requireConsent(me: MeResponse, pathname: string): void {
  if (me.pendingConsents.length > 0 && pathname !== CONSENT_ROUTE) {
    throw redirect({ to: CONSENT_ROUTE });
  }
}

/**
 * Returns the organization the shell should run in.
 *
 * En person uten medlemskap sendes til en side som sier nettopp det. Den PLEIDE å være «opprett
 * organisasjon», men et hus lages bare på invitasjon nå (sak B3), så det siden kan tilby er å
 * forklare tilstanden. Å stå uten hus er fortsatt en tilstand og ikke en feil.
 */
export function requireMembership(me: MeResponse): string {
  /*
   * GJESTEMODUS (sak 232, T5): fanen står i organisasjonen som delte, og serveren har sagt `guest`
   * for den. Personens egen organisasjon (localStorage) røres ikke — den kommer tilbake med «Tilbake».
   */
  const guest = getGuestOrganizationId();
  if (guest !== null && me.activeOrgRole === 'guest' && me.activeOrganizationId === guest) {
    return guest;
  }
  const first = me.memberships.at(0);
  if (!first) {
    throw redirect({ to: NO_ORGANIZATION_ROUTE });
  }
  const active =
    me.activeOrganizationId !== null && isMembershipOf(me, me.activeOrganizationId)
      ? me.activeOrganizationId
      : first.organizationId;
  setActiveOrganizationId(active);
  return active;
}

/**
 * The refusal behind a hidden menu entry (decision 0005, step 4).
 *
 * "A hidden menu item is UX, never security": someone who types `/contacts` must meet the same
 * answer the API gives them. This is the browser saying it early — the API says it again on
 * every request, and it is the API that decides (principle 7).
 *
 * It throws the very `ApiError` shape a 403 from the wire produces, so `RouteError` renders
 * `errors.code.forbidden` and the user reads one sentence rather than a policy trace. The empty
 * request id is deliberate: nothing reached the server, so there is no id to quote to support.
 */
export function requirePermission(
  me: MeResponse,
  action: Action,
  /** Modulen ruta hører til, når handlingen deles av flere (G1): `venue:list` i Spillesteder. */
  inModule?: OrganizationModule,
): void {
  if (reachesIn(actorFromMe(me), action, inModule)) return;
  throw new ApiError(403, 'FORBIDDEN', `This route requires "${action}".`, '');
}

/**
 * Den strenge varianten: rollen må ha HELE samlingen, ikke bare en rad av den.
 *
 * Laget for den gamle rollen `external` (KOI Del 2: årgangens Dokumenter og Kart). Siden sak 232 har
 * alle fire rollene `edition:read`, og en gjest slipper inn på en samling på nivå «se» — en delt
 * avdeling viser dokumentene sine (spesifikasjonen §4), og RLS snevrer radene til det delte.
 * `requirePermission` slipper siden QA runde 5 inn alle som kan nå i det minste én rad (H5/H7).
 */
/**
 * Plattformsperren (QA runde 5, H1/H2; sak 232): en gjest ser aldri mer enn det som er delt.
 * Organisasjonens egne flater — bookingregisteret og Innstillinger — er aldri delt, så ruta sier nei
 * med navn. API-et sier det samme (`@NotForGuest`).
 */
export function refuseGuest(me: MeResponse): void {
  if (me.activeOrgRole !== 'guest') return;
  throw new ApiError(403, 'FORBIDDEN', 'This page is not open to a guest.', '');
}

export function requireCollectionPermission(me: MeResponse, action: Action): void {
  if (allows(actorFromMe(me), action)) return;
  throw new ApiError(403, 'FORBIDDEN', `This route requires "${action}".`, '');
}
